import React, { useState } from 'react';

const getStatusStyle = (status) => {
  switch (status) {
    case 'no-show':
      return 'bg-gray-600/50 text-gray-300 cursor-not-allowed';
    case 'private':
      return 'bg-amber-600/50 text-amber-300 cursor-not-allowed';
    case 'available':
      return 'bg-purple-600 text-white hover:bg-purple-700 hover:scale-105 cursor-pointer';
    default:
      return 'bg-gray-700/30 text-gray-500 cursor-default';
  }
};

const getStatusTitle = (status, sublabel, displayDate) => {
  switch (status) {
    case 'no-show':
      return sublabel ? `No Show - ${sublabel}` : 'No Show';
    case 'private':
      return 'Private Show';
    case 'available':
      return `Available - ${sublabel || ''} - ${displayDate}`;
    default:
      return '';
  }
};

const buildCalendarDays = (currentMonth, saturdayStatuses) => {
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const lastDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
  const startingDayOfWeek = firstDayOfMonth.getDay();
  const daysInMonth = lastDayOfMonth.getDate();
  const days = [];

  for (let i = 0; i < startingDayOfWeek; i++) {
    days.push(null);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dateString = date.toISOString().split('T')[0];
    const saturdayInfo = saturdayStatuses[dateString];

    days.push({
      day,
      date: dateString,
      hasStatus: !!saturdayInfo,
      status: saturdayInfo?.status || null,
      label: saturdayInfo?.label || null,
      sublabel: saturdayInfo?.sublabel || null,
      displayDate: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    });
  }

  return days;
};

const CalendarComponent = ({ onDateSelection }) => {
  const [currentMonth] = useState(new Date(2026, 10, 1)); // November 2026

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // November 2026 Saturday statuses
  const saturdayStatuses = {
    '2026-11-07': { status: 'no-show', label: 'No Show', sublabel: '' },
    '2026-11-10': { status: 'available', label: 'Available', sublabel: 'Tickets On Sale' },
    '2026-11-14': { status: 'no-show', label: 'No Show', sublabel: '' },
    '2026-11-21': { status: 'no-show', label: 'No Show', sublabel: '' },
    '2026-11-26': { status: 'no-show', label: 'No Show', sublabel: 'Thanksgiving' },
    '2026-11-28': { status: 'no-show', label: 'No Show', sublabel: '' },
  };

  const calendarDays = buildCalendarDays(currentMonth, saturdayStatuses);

  const handleDateClick = (dateData) => {
    if (dateData.status === 'available') {
      onDateSelection(dateData.date);
    }
  };

  return (
    <div className="bg-gray-900/30 p-6 rounded-lg border border-gray-700">
      {/* Calendar Header */}
      <div className="flex items-center justify-center mb-6">
        <h3 className="text-2xl font-bold text-white">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
      </div>

      {/* Days of week header */}
      <div className="grid grid-cols-7 gap-2 mb-4">
        {daysOfWeek.map((day) => (
          <div key={day} className="text-center text-gray-400 font-semibold py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-2">
        {calendarDays.map((dateData, index) => {
          if (!dateData) {
            return <div key={`empty-${index}`} className="h-16"></div>;
          }

          const { day, date, hasStatus, status, label, sublabel, displayDate } = dateData;
          const cellClass = hasStatus && status ? getStatusStyle(status) : 'bg-gray-700/30 text-gray-500 cursor-default';
          const titleText = hasStatus && status ? getStatusTitle(status, sublabel, displayDate) : '';
          
          return (
            <button
              key={date}
              onClick={() => handleDateClick(dateData)}
              disabled={status !== 'available'}
              className={`h-16 w-full rounded-md text-sm font-semibold transition-all duration-200 ${cellClass}`}
              title={titleText}
            >
              {day}
              {hasStatus && label && (
                <div className="text-[9px] leading-tight mt-0.5">
                  {label}
                  {sublabel && <div>{sublabel}</div>}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-6 flex flex-wrap justify-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-purple-600 rounded"></div>
          <span className="text-white">Available</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-amber-600/50 rounded"></div>
          <span className="text-white">Private Show</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-600/50 rounded"></div>
          <span className="text-white">No Show</span>
        </div>
      </div>
    </div>
  );
};

export default CalendarComponent;
